import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    FormControlLabel,
    Grid,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Popover,
    Switch,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import CropFreeIcon from '@mui/icons-material/CropFree';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PlaceIcon from '@mui/icons-material/Place';

import config from '../config';
import { CREATE, DataAPIContext, GET_ONE, UPDATE } from '../api';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import ImageActionButton from '../components/ImageActionButton';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import SystemTagsDropdown from '../components/SystemTagsDropdown';
import TimeOfDayPicker from '../components/TimeOfDayPicker';
import useLeavePrompt, { LEAVE_WITHOUT_SAVING_MESSAGE } from '../components/useLeavePrompt';
import { ContextStore } from '../store/contextStore';
import Image from 'mui-image';

import BusStopsMap from './BusStopsMap';
import LineSettingsDialog from './LineSettingsDialog';
import StopWarningIcon from './StopWarningIcon';
import { toCoord } from './utils';
import imagesAPI from '../components/images';
import LatLngPicker from '../components/LatLngPicker';
import UsedByPanel from '../components/UsedByPanel';

const VEHICLE_STATUS_LABELS = {
    moving: 'Moving',
    stopped: 'Stopped',
    idle: 'Idle',
    offline: 'Offline',
};

const VEHICLE_STATUS_CHIP_COLORS = {
    moving: 'success',
    stopped: 'warning',
    idle: 'default',
    offline: 'error',
};

const formatAge = (ts) => {
    if (!ts) return 'Unknown';
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

const LineSetEdit = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { settings, token, hasPerm, isFranchise, currentLicensee } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('vehicle_fleet', 'write');
    const history = useHistory();
    const location = useLocation();
    const { id } = useParams();
    const isNew = !id;
    const { enqueueSnackbar } = useSnackbar();

    // URL state — the selected route tab and any open drawers are reflected in the
    // query string so the page can be deep-linked / refreshed without losing context.
    //   ?route=<route_id>     active line tab (matched against route.id)
    //   ?settings=1           line settings drawer open for the active route
    //   ?stop=<stop_id|new>   stop drawer open (existing stop id or 'new' for add)
    const queryParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
    const urlRouteId = queryParams.get('route');
    const urlSettings = queryParams.get('settings') === '1';
    const urlStop = queryParams.get('stop');

    const updateUrlParams = React.useCallback(
        (updates) => {
            const next = new URLSearchParams(location.search);
            Object.entries(updates).forEach(([k, v]) => {
                if (v == null || v === '' || v === false) next.delete(k);
                else next.set(k, String(v));
            });
            const search = next.toString();
            history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
        },
        [history, location.pathname, location.search]
    );

    const [loading, setLoading] = React.useState(!isNew);
    const [saving, setSaving] = React.useState(false);
    const [lineSet, setLineSet] = React.useState({
        name: '',
        atag: [],
        tags: [],
        routes: [],
        tour_operator: currentLicensee || null,
    });
    // The line set as last loaded or saved; the leave prompt compares against it.
    const [savedLineSet, setSavedLineSet] = React.useState(lineSet);
    const [activeTab, setActiveTab] = React.useState(0);
    const [lineSettingsOpen, setLineSettingsOpen] = React.useState(false);
    const [lineSettingsDirty, setLineSettingsDirty] = React.useState(false);
    const [lineSettingsRouteIndex, setLineSettingsRouteIndex] = React.useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [editingStop, setEditingStop] = React.useState(null);
    const [addingStop, setAddingStop] = React.useState(false);
    const [existingStopSelection, setExistingStopSelection] = React.useState(null);
    const emptyStopForm = {
        name: '', latitude: '', longitude: '', label: '', start_time: '', end_time: '',
        description: '', warning: '',
        // Display URL for the <img> preview (blob: for new uploads, S3 URL for existing).
        image: null,
        // Pending change sentinel: undefined = no change, File = new upload, null = clear.
        image_file: undefined,
        loop_offset: 0,
        first_departure: '',
        last_full_loop: '',
        last_departure: '',
        attractions: [],
    };
    const [editStopForm, setEditStopForm] = React.useState(emptyStopForm);
    // The stop form as the drawer opened it, for the drawer's own leave prompt.
    const [openedStopForm, setOpenedStopForm] = React.useState(emptyStopForm);
    const openStopForm = (form) => {
        setEditStopForm(form);
        setOpenedStopForm(form);
    };

    const onLicenseeChange = (id) => {
        setLineSet((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    // Map layer mode: 'stops' (default), 'vehicles', 'both'
    const [mapLayer, setMapLayer] = React.useState('stops');
    const [vehicles, setVehicles] = React.useState([]);
    const [refreshCountdown, setRefreshCountdown] = React.useState(null);
    const [showKml, setShowKml] = React.useState(true);
    const [mapViewportDirty, setMapViewportDirty] = React.useState(false);
    const [resetViewportKey, setResetViewportKey] = React.useState(0);
    const [vehicleListAnchor, setVehicleListAnchor] = React.useState(null);
    const [showAllRouteVehicles, setShowAllRouteVehicles] = React.useState(false);
    const [showOutOfBounds, setShowOutOfBounds] = React.useState(false);

    const stopDrawerOpen = editingStop !== null || addingStop;
    const closeStopDrawer = () => {
        setEditingStop(null);
        setAddingStop(false);
        setExistingStopSelection(null);
        openStopForm(emptyStopForm);
        updateUrlParams({ stop: null });
    };

    const comparableStopForm = (form) => ({ ...form, latitude: toCoord(form.latitude), longitude: toCoord(form.longitude) });
    const stopFormDirty = stopDrawerOpen && !isEqual(comparableStopForm(editStopForm), comparableStopForm(openedStopForm));

    // X, CANCEL and the backdrop. The drawer closes with a same-page URL
    // replace, which the page's leave prompt lets through, so it asks here.
    const requestCloseStopDrawer = () => {
        if (stopFormDirty && hasWritePerm && !window.confirm(LEAVE_WITHOUT_SAVING_MESSAGE)) return;
        closeStopDrawer();
    };

    // Leaving the page (back arrow, CANCEL, sidebar, browser back) with unsaved
    // edits in the line set or in an open drawer asks first. Query-string
    // replaces on this page only open and close the drawers.
    const hasChanges = hasWritePerm && (!isEqual(lineSet, savedLineSet) || stopFormDirty || lineSettingsDirty);
    const leaveWithoutPrompt = useLeavePrompt(hasChanges, {
        isIgnored: (nextLocation) => nextLocation.pathname === location.pathname,
    });

    // The backend now returns routes as link wrappers: [{id, sequence, route: {...}}].
    // Internally we keep the flat shape (each item is the route directly) and stash
    // the link metadata under _link_id / _link_sequence so onSave can rebuild the
    // wrapper. Falls back to the legacy flat shape for forward/back-compat.
    const normalizeRoutes = (routesFromApi) =>
        (routesFromApi || []).map((entry, idx) => {
            if (entry && typeof entry === 'object' && entry.route && typeof entry.route === 'object') {
                return {
                    ...entry.route,
                    _link_id: entry.id ?? null,
                    _link_sequence: entry.sequence ?? idx + 1,
                };
            }
            return { ...entry, _link_id: entry?.id_link ?? null, _link_sequence: idx + 1 };
        });

    React.useEffect(() => {
        if (!isNew) {
            setLoading(true);
            dataAPI(GET_ONE, 'route_set', { id })
                .then((res) => {
                    const data = res.data;
                    const loaded = {
                        ...data,
                        atag: data.atag || [],
                        tags: data.tags || [],
                        routes: normalizeRoutes(data.routes),
                    };
                    setLineSet(loaded);
                    setSavedLineSet(loaded);
                })
                .catch(() => enqueueSnackbar('Error loading line set', { variant: 'error' }))
                .finally(() => setLoading(false));
        }
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync activeTab from ?route=<id> in URL whenever the URL or routes list changes.
    React.useEffect(() => {
        if (!lineSet.routes?.length) return;
        if (urlRouteId == null) return;
        const idx = lineSet.routes.findIndex((r) => String(r.id) === urlRouteId);
        if (idx >= 0 && idx !== activeTab) setActiveTab(idx);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlRouteId, lineSet.routes]);

    // Sync line settings drawer from ?settings=1
    React.useEffect(() => {
        if (!lineSet.routes?.length) return;
        if (urlSettings) {
            if (!lineSettingsOpen) {
                setLineSettingsRouteIndex(activeTab);
                setLineSettingsOpen(true);
            } else if (lineSettingsRouteIndex !== activeTab) {
                // activeTab caught up to ?route=<id> after the drawer was already
                // opened on the initial render — point the dialog at the correct route.
                setLineSettingsRouteIndex(activeTab);
            }
        } else if (lineSettingsOpen) {
            setLineSettingsOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlSettings, lineSet.routes, activeTab]);

    // Sync stop drawer from ?stop=<id|new>
    React.useEffect(() => {
        const activeRouteForUrl = lineSet.routes?.[activeTab];
        if (urlStop === 'new') {
            if (!addingStop) {
                setAddingStop(true);
                setExistingStopSelection(null);
                openStopForm(emptyStopForm);
                setEditingStop(null);
            }
        } else if (urlStop) {
            if (!activeRouteForUrl) return;
            const idx = (activeRouteForUrl.stops || []).findIndex(
                (link) => String(link?.stop?.id) === urlStop
            );
            if (idx >= 0 && editingStop !== idx) {
                // Inline the openStopEdit logic so we don't push another URL update
                setAddingStop(false);
                setExistingStopSelection(null);
                setEditingStop(idx);
                const link = activeRouteForUrl.stops[idx];
                const stopImg = link.stop?.image_preview || link.stop?.image || null;
                openStopForm({
                    name: link.stop?.name || '',
                    latitude: link.stop?.latitude ?? '',
                    longitude: link.stop?.longitude ?? '',
                    label: link.stop?.label ?? '',
                    start_time: link.stop?.start_time || '',
                    end_time: link.stop?.end_time || '',
                    description: link.stop?.description || '',
                    warning: link.stop?.warning || '',
                    image: stopImg,
                    image_file: link.stop?.image_file,
                    loop_offset: link.loop_offset ?? 0,
                    first_departure: link.first_departure || '',
                    last_full_loop: link.last_full_loop || '',
                    last_departure: link.last_departure || '',
                    attractions: (link.stop?.attractions || []).map((a, idx) => ({
                        attraction_id: a.attraction_id,
                        name: a.name || '',
                        sequence: a.sequence ?? idx + 1,
                        distance: a.distance != null ? String(a.distance) : '',
                    })),
                });
            }
        } else if (editingStop !== null || addingStop) {
            setEditingStop(null);
            setAddingStop(false);
            setExistingStopSelection(null);
            openStopForm(emptyStopForm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlStop, lineSet.routes, activeTab]);

    // Poll vehicle positions when the map layer includes vehicles.
    const POLL_INTERVAL = 15;
    const pollIntervalRef = React.useRef(null);
    const pollCancelledRef = React.useRef(false);

    const fetchPositionsNow = React.useCallback(() => {
        if (!id || pollCancelledRef.current) return;
        setRefreshCountdown(POLL_INTERVAL);
        const baseUrl = config.apiUrl;
        const qs = showOutOfBounds ? '?all_vehicles=1' : '';
        const url = `${baseUrl}/route_set/${id}/vehicle_positions/${qs}`;
        fetch(url, {
            headers: {
                Accept: 'application/json',
                Authorization: `Token ${token}`,
            },
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (!pollCancelledRef.current && data?.vehicles) {
                    setVehicles(data.vehicles);
                }
            })
            .catch(() => {});
    }, [id, token, showOutOfBounds]);

    const refreshVehiclesNow = React.useCallback(() => {
        // Clear and restart the polling interval so the next auto-refresh
        // is a full POLL_INTERVAL from now.
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        fetchPositionsNow();
        pollIntervalRef.current = setInterval(fetchPositionsNow, POLL_INTERVAL * 1000);
    }, [fetchPositionsNow]);

    React.useEffect(() => {
        if (mapLayer === 'stops') {
            setVehicles([]);
            setRefreshCountdown(null);
            return;
        }
        if (!id) return;

        pollCancelledRef.current = false;
        fetchPositionsNow();
        pollIntervalRef.current = setInterval(fetchPositionsNow, POLL_INTERVAL * 1000);
        return () => {
            pollCancelledRef.current = true;
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setRefreshCountdown(null);
        };
    }, [mapLayer, id, token, fetchPositionsNow]);

    // Tick the countdown every second while polling is active.
    React.useEffect(() => {
        if (refreshCountdown == null) return;
        if (refreshCountdown <= 0) return;
        const tm = setTimeout(() => setRefreshCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
        return () => clearTimeout(tm);
    }, [refreshCountdown]);

    const onSave = () => {
        const files = {};
        const isFileLike = (v) =>
            v && typeof v === 'object' && typeof v.name === 'string' && typeof v.size === 'number';
        (lineSet.routes || []).forEach((route, routeIndex) => {
            if (isFileLike(route.kml_file)) {
                files[`kml_route_${routeIndex}`] = route.kml_file;
            }
            (route.stops || []).forEach((link, stopIndex) => {
                if (isFileLike(link?.stop?.image_file)) {
                    files[`image_route_${routeIndex}_stop_${stopIndex}`] = link.stop.image_file;
                }
            });
        });
        const body = {
            name: lineSet.name,
            atag: lineSet.atag || [],
            tags: lineSet.tags || [],
            ...(lineSet.tour_operator ? { tour_operator: typeof lineSet.tour_operator === 'object' ? lineSet.tour_operator.id : lineSet.tour_operator } : {}),
            // Backend wire shape: routes are link wrappers {id, sequence, route: {...}}.
            // sequence is taken from the current array position so reorder is persisted.
            routes: lineSet.routes?.map((route, routeIdx) => ({
                ...(route._link_id != null ? { id: route._link_id } : {}),
                sequence: routeIdx + 1,
                route: {
                    id: route.id,
                    name: route.name,
                    color: route.color,
                    enabled: route.enabled,
                    start_time: route.start_time,
                    end_time: route.end_time,
                    loop_duration: route.loop_duration,
                    warning: route.warning,
                    frequency: route.frequency,
                    // KML clear: explicit null → tell backend to clear; new File → sent via multipart; undefined → leave alone
                    ...(route.kml_file === null ? { kml_file: null } : {}),
                    stops: route.stops?.map((link) => ({
                        ...(link.id != null ? { id: link.id } : {}),
                        sequence: link.sequence,
                        loop_offset: link.loop_offset ?? 0,
                        first_departure: link.first_departure || null,
                        last_full_loop: link.last_full_loop || null,
                        last_departure: link.last_departure || null,
                        stop: {
                            id: link.stop?.id,
                            name: link.stop?.name,
                            latitude: link.stop?.latitude,
                            longitude: link.stop?.longitude,
                            label: link.stop?.label,
                            description: link.stop?.description,
                            warning: link.stop?.warning,
                            start_time: link.stop?.start_time,
                            end_time: link.stop?.end_time,
                            enabled: link.stop?.enabled,
                            // Image clear: explicit null → tell backend to drop image_id;
                            // new File → sent via multipart and backend assigns image_id;
                            // undefined / URL → leave alone.
                            ...(link.stop?.image_file === null ? { image_id: null } : {}),
                            attractions: (link.stop?.attractions || [])
                                .filter((a) => a.attraction_id != null)
                                .map((a, idx) => ({
                                    attraction_id: a.attraction_id,
                                    sequence: idx + 1,
                                    distance: a.distance !== '' && a.distance != null ? parseInt(a.distance, 10) : 0,
                                })),
                        },
                    })),
                    breaks: route.breaks?.map((b) => ({
                        id: b.id,
                        start_time: b.start_time,
                        end_time: b.end_time,
                    })),
                },
            })),
        };

        setSaving(true);
        const action = isNew ? CREATE : UPDATE;
        const hasFiles = Object.keys(files).length > 0;
        const uploadParams = hasFiles ? { files, upload: true } : {};
        const params = isNew
            ? { data: body, ...uploadParams }
            : { id, data: body, ...uploadParams };

        dataAPI(action, 'route_set', params)
            .then((res) => {
                if (res.statusCode && res.statusCode >= 400) {
                    enqueueSnackbar(res.data?.message || 'Error saving line set', { variant: 'error' });
                } else {
                    enqueueSnackbar(isNew ? 'Line set created' : 'Line set saved', { variant: 'default' });
                    if (isNew && res.data?.id) {
                        leaveWithoutPrompt(() => history.replace(`/bus-tours/line-sets/${res.data.id}/edit`));
                    } else if (res.data && Array.isArray(res.data.routes)) {
                        // Refresh local state from the server response to ensure
                        // the UI reflects what was actually persisted. This also
                        // naturally clears any pending file sentinels (image_file,
                        // kml_file) since they don't exist in the API response.
                        const saved = {
                            ...res.data,
                            atag: res.data.atag || [],
                            tags: res.data.tags || [],
                            routes: normalizeRoutes(res.data.routes),
                        };
                        setLineSet(saved);
                        setSavedLineSet(saved);
                    }
                }
            })
            .catch(() => enqueueSnackbar('Error saving line set', { variant: 'error' }))
            .finally(() => setSaving(false));
    };

    const onDeleteLineSet = () => {
        dataAPI('DELETE', 'route_set', { id })
            .then(() => {
                enqueueSnackbar('Line set deleted', { variant: 'default' });
                leaveWithoutPrompt(() => history.push('/bus-tours/line-sets'));
            })
            .catch(() => enqueueSnackbar('Error deleting line set', { variant: 'error' }));
    };

    const addNewLine = () => {
        const newRoute = {
            name: `Line ${lineSet.routes.length + 1}`,
            color: '#007AFF',
            enabled: true,
            start_time: null,
            end_time: null,
            loop_duration: null,
            stops: [],
            breaks: [],
        };
        setLineSet((prev) => ({
            ...prev,
            routes: [...prev.routes, newRoute],
        }));
        setActiveTab(lineSet.routes.length);
    };

    const removeLine = (index) => {
        setLineSet((prev) => ({
            ...prev,
            routes: prev.routes.filter((_, i) => i !== index),
        }));
        if (activeTab >= lineSet.routes.length - 1) {
            setActiveTab(Math.max(0, lineSet.routes.length - 2));
        }
    };

    const moveLine = (index, direction) => {
        const target = direction === 'left' ? index - 1 : index + 1;
        if (target < 0 || target >= lineSet.routes.length) return;
        setLineSet((prev) => {
            const next = [...prev.routes];
            [next[index], next[target]] = [next[target], next[index]];
            return { ...prev, routes: next };
        });
        // Keep the moved tab focused as it slides under the cursor
        if (activeTab === index) setActiveTab(target);
        else if (activeTab === target) setActiveTab(index);
    };

    const openLineSettings = (index) => {
        setLineSettingsRouteIndex(index);
        setLineSettingsOpen(true);
        updateUrlParams({
            route: lineSet.routes[index]?.id ?? null,
            settings: '1',
        });
    };

    const onLineSettingsSave = (updatedRoute) => {
        setLineSet((prev) => ({
            ...prev,
            routes: prev.routes.map((r, i) => (i === lineSettingsRouteIndex ? { ...r, ...updatedRoute } : r)),
        }));
        setLineSettingsOpen(false);
        updateUrlParams({ settings: null });
    };

    const updateRouteEnabled = (index, enabled) => {
        setLineSet((prev) => ({
            ...prev,
            routes: prev.routes.map((r, i) => (i === index ? { ...r, enabled } : r)),
        }));
    };

    const updateStops = (routeIndex, updater) => {
        setLineSet((prev) => ({
            ...prev,
            routes: prev.routes.map((r, i) =>
                i === routeIndex ? { ...r, stops: updater(r.stops || []) } : r
            ),
        }));
    };

    const removeStop = (stopIndex) => {
        updateStops(activeTab, (stops) => stops.filter((_, i) => i !== stopIndex));
    };

    const moveStop = (stopIndex, direction) => {
        const targetIndex = direction === 'up' ? stopIndex - 1 : stopIndex + 1;
        updateStops(activeTab, (stops) => {
            const updated = [...stops];
            [updated[stopIndex], updated[targetIndex]] = [updated[targetIndex], updated[stopIndex]];
            return updated.map((s, i) => ({ ...s, sequence: i + 1 }));
        });
    };

    const openStopAdd = () => {
        setAddingStop(true);
        setExistingStopSelection(null);
        openStopForm(emptyStopForm);
        updateUrlParams({ stop: 'new' });
    };

    const openStopEdit = (stopIndex) => {
        const link = lineSet.routes[activeTab]?.stops?.[stopIndex];
        if (!link) return;
        setAddingStop(false);
        setExistingStopSelection(null);
        setEditingStop(stopIndex);
        updateUrlParams({ stop: link.stop?.id ?? 'new' });
        // `image` holds the raw source the renderer will resolve:
        //   - blob: URL string  → freshly picked file (use directly)
        //   - { p, s } object   → backend image (resolve via images.url)
        // If the user already picked a file in this session, the cached blob URL
        // on link.stop.image_preview takes precedence.
        const imageSource = link.stop?.image_preview || link.stop?.image || null;
        openStopForm({
            name: link.stop?.name || '',
            latitude: link.stop?.latitude ?? '',
            longitude: link.stop?.longitude ?? '',
            label: link.stop?.label ?? '',
            start_time: link.stop?.start_time || '',
            end_time: link.stop?.end_time || '',
            description: link.stop?.description || '',
            warning: link.stop?.warning || '',
            image: imageSource,
            image_file: link.stop?.image_file,
            loop_offset: link.loop_offset ?? 0,
            first_departure: link.first_departure || '',
            last_full_loop: link.last_full_loop || '',
            last_departure: link.last_departure || '',
            attractions: (link.stop?.attractions || []).map((a, idx) => ({
                attraction_id: a.attraction_id,
                name: a.name || '',
                sequence: a.sequence ?? idx + 1,
                distance: a.distance != null ? String(a.distance) : '',
            })),
        });
    };

    const buildStopFromForm = (existingStop = {}) => ({
        ...existingStop,
        ...(existingStopSelection?.id ? { id: existingStopSelection.id } : {}),
        name: editStopForm.name,
        latitude: editStopForm.latitude !== '' ? parseFloat(editStopForm.latitude) : null,
        longitude: editStopForm.longitude !== '' ? parseFloat(editStopForm.longitude) : null,
        label: editStopForm.label || null,
        start_time: editStopForm.start_time || null,
        end_time: editStopForm.end_time || null,
        description: editStopForm.description,
        warning: editStopForm.warning,
        // Image upload state. `image_file` is the pending change sentinel,
        // `image_preview` is the blob URL kept around so the drawer can re-display
        // the just-uploaded image when the stop is reopened before save.
        // `image` is the original {p, s} object from the backend — preserved so the
        // drawer can resolve and display it when the stop is reopened.
        image: editStopForm.image_file === null ? null : (editStopForm.image ?? existingStop.image),
        image_file: editStopForm.image_file,
        image_preview: editStopForm.image_file instanceof File ? editStopForm.image : undefined,
        attractions: editStopForm.attractions,
    });

    const saveStopEdit = () => {
        if (addingStop) {
            const currentStops = lineSet.routes[activeTab]?.stops || [];
            const newLink = {
                sequence: currentStops.length + 1,
                loop_offset: editStopForm.loop_offset ?? 0,
                first_departure: editStopForm.first_departure || null,
                last_full_loop: editStopForm.last_full_loop || null,
                last_departure: editStopForm.last_departure || null,
                stop: buildStopFromForm(),
            };
            updateStops(activeTab, (stops) => [...stops, newLink]);
        } else {
            const editedStop = buildStopFromForm(lineSet.routes[activeTab]?.stops?.[editingStop]?.stop);
            const editedStopId = editedStop.id;
            const editedLinkFields = {
                loop_offset: editStopForm.loop_offset ?? 0,
                first_departure: editStopForm.first_departure || null,
                last_full_loop: editStopForm.last_full_loop || null,
                last_departure: editStopForm.last_departure || null,
            };
            // A stop can appear in multiple routes. Sync the edited stop's
            // data to every occurrence so the next main save sends consistent
            // data and the last-processed route doesn't overwrite the edit.
            // Note: link fields (loop_offset, departure times) are per-link,
            // so only update them on the current link.
            setLineSet((prev) => ({
                ...prev,
                routes: prev.routes.map((route, routeIdx) => ({
                    ...route,
                    stops: (route.stops || []).map((link, linkIdx) => {
                        if (routeIdx === activeTab && linkIdx === editingStop) {
                            return { ...link, stop: editedStop, ...editedLinkFields };
                        }
                        if (editedStopId != null && link.stop?.id === editedStopId) {
                            return { ...link, stop: { ...link.stop, ...editedStop } };
                        }
                        return link;
                    }),
                })),
            }));
        }
        closeStopDrawer();
    };

    const activeRoute = lineSet.routes?.[activeTab];

    // The selected tab gets exactly one highlight color: the line's own color,
    // carried by the MUI tab indicator. Falls back to the theme blue for lines
    // with no color set and for the "Used by" tab.
    const activeTabColor = activeRoute?.color || '#007AFF';

    // Build a map of route_id → color for vehicle marker coloring.
    const routeColors = React.useMemo(() => {
        const m = {};
        (lineSet.routes || []).forEach((r) => { if (r.id != null) m[r.id] = r.color || '#007AFF'; });
        return m;
    }, [lineSet.routes]);

    // Merged bounding box across all routes in the set (union).
    const routeSetBoundingBox = React.useMemo(() => {
        const boxes = (lineSet.routes || []).map((r) => r.bounding_box).filter(Boolean);
        if (boxes.length === 0) return null;
        return {
            north: Math.max(...boxes.map((b) => b.north)),
            east: Math.max(...boxes.map((b) => b.east)),
            south: Math.min(...boxes.map((b) => b.south)),
            west: Math.min(...boxes.map((b) => b.west)),
        };
    }, [lineSet.routes]);

    // Filter vehicles to the active route unless "all" is toggled on.
    const visibleVehicles = React.useMemo(() => {
        if (showAllRouteVehicles || !activeRoute?.id) return vehicles;
        return vehicles.filter((v) => v.route_id === activeRoute.id);
    }, [vehicles, showAllRouteVehicles, activeRoute]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(lineSet.tour_operator)}>
            <PageMeta title={isNew ? 'Create New Line Set' : lineSet.name || 'Edit Line Set'} />
            <FixedLinearProgress visibility={saving} />
            {deleteConfirmOpen && (
                <ConfirmationDialog
                    keepMounted
                    open
                    onCancel={() => setDeleteConfirmOpen(false)}
                    onConfirm={onDeleteLineSet}
                    title="Delete line set?"
                    okButtonText="Delete"
                    cancelButtonText="Cancel"
                >
                    Are you sure you want to delete this line set and all its routes?
                </ConfirmationDialog>
            )}
            <Drawer anchor="right" open={stopDrawerOpen} onClose={requestCloseStopDrawer} PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, overflowY: 'auto' } }}>
                <Box>
                    <Box sx={{ bgcolor: '#f5f5f5', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 2 }}>
                        <Typography variant="h6">{addingStop ? 'Add stop' : 'Edit stop'}</Typography>
                        <IconButton onClick={requestCloseStopDrawer}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {addingStop && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    Search existing stops
                                </Typography>
                                <RemoteAutocomplete
                                    resource="route_stop"
                                    urlSuffix={null}
                                    label="Search by stop name..."
                                    variant="filled"
                                    size="small"
                                    value={existingStopSelection}
                                    onChange={(ev, value) => {
                                        setExistingStopSelection(value || null);
                                        if (value) {
                                            setEditStopForm({
                                                name: value.name || '',
                                                latitude: value.latitude ?? '',
                                                longitude: value.longitude ?? '',
                                                label: value.label ?? '',
                                                start_time: value.start_time || '',
                                                end_time: value.end_time || '',
                                                description: value.description || '',
                                                warning: value.warning || '',
                                                image: value.image || null,
                                                loop_offset: 0,
                                                first_departure: '',
                                                last_full_loop: '',
                                                last_departure: '',
                                                attractions: (value.attractions || []).map((a, idx) => ({
                                                    attraction_id: a.attraction_id,
                                                    name: a.name || '',
                                                    sequence: a.sequence ?? idx + 1,
                                                    distance: a.distance != null ? String(a.distance) : '',
                                                })),
                                            });
                                        } else {
                                            setEditStopForm(emptyStopForm);
                                        }
                                    }}
                                    getOptionLabel={(opt) => opt?.name || ''}
                                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                    optional
                                />
                            </Box>
                        )}
                        {addingStop && !existingStopSelection && (
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: 1 }}>
                                Create new stop
                            </Typography>
                        )}
                        <TextField
                            variant="filled"
                            label="Stop Name"
                            value={editStopForm.name}
                            onChange={(e) => { const { value } = e.target; setEditStopForm((p) => ({ ...p, name: value })); }}
                            fullWidth
                            size="small"
                        />
                        <LatLngPicker
                            latitude={editStopForm.latitude}
                            longitude={editStopForm.longitude}
                            mapHeight={175}
                            onUpdate={({ latitude: lat, longitude: lng }) =>
                                setEditStopForm((p) => ({ ...p, latitude: lat ?? '', longitude: lng ?? '' }))
                            }
                        />
                        <TextField
                            variant="filled"
                            label="Pin Label"
                            value={editStopForm.label}
                            onChange={(e) => setEditStopForm((p) => ({ ...p, label: e.target.value.slice(0, 3) }))}
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 3 }}
                        />
                        <TextField
                            variant="filled"
                            label="Loop Offset"
                            type="number"
                            value={editStopForm.loop_offset}
                            onChange={(e) => setEditStopForm((p) => ({
                                ...p,
                                loop_offset: e.target.value !== '' ? parseInt(e.target.value, 10) : 0,
                            }))}
                            fullWidth
                            size="small"
                            helperText="Offset within loop cycle (minutes)"
                            InputLabelProps={{ shrink: true }}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TimeOfDayPicker
                                    label="First Departure"
                                    value={editStopForm.first_departure}
                                    onChange={(v) => setEditStopForm((p) => ({ ...p, first_departure: v }))}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TimeOfDayPicker
                                    label="Last Full Loop"
                                    value={editStopForm.last_full_loop}
                                    onChange={(v) => setEditStopForm((p) => ({ ...p, last_full_loop: v }))}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TimeOfDayPicker
                                    label="Last Departure"
                                    value={editStopForm.last_departure}
                                    onChange={(v) => setEditStopForm((p) => ({ ...p, last_departure: v }))}
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            variant="filled"
                            label="Description (Optional)"
                            value={editStopForm.description}
                            onChange={(e) => { const { value } = e.target; setEditStopForm((p) => ({ ...p, description: value })); }}
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                        />
                        <TextField
                            variant="filled"
                            label="Warning"
                            value={editStopForm.warning}
                            onChange={(e) => { const { value } = e.target; setEditStopForm((p) => ({ ...p, warning: value })); }}
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                        />
                        {editStopForm.image && (() => {
                            const STOP_IMG_W = 368;
                            const STOP_IMG_H = 272;
                            const isLocalUrl = typeof editStopForm.image === 'string';
                            const resolvedSrc = isLocalUrl
                                ? editStopForm.image
                                : imagesAPI.url(settings?.images?.base, editStopForm.image, `cover_${STOP_IMG_W}x${STOP_IMG_H}`);
                            return (
                            <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                                <Image
                                    src={resolvedSrc}
                                    cover
                                    height={STOP_IMG_H}
                                    width="100%"
                                    style={{ borderRadius: 8, display: 'block' }}
                                />
                                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                                    <DeleteIconButton
                                        title="Delete image"
                                        overImage
                                        allowed={hasWritePerm}
                                        onClick={() => setEditStopForm((p) => ({ ...p, image: null, image_file: null }))}
                                    />
                                </Box>
                                <ImageActionButton
                                    hasImage
                                    overImage
                                    component="label"
                                    disabled={!hasWritePerm}
                                    sx={{ position: 'absolute', bottom: 12, right: 12 }}
                                >
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) setEditStopForm((p) => ({ ...p, image: URL.createObjectURL(file), image_file: file }));
                                        }}
                                    />
                                </ImageActionButton>
                            </Box>
                            );
                        })()}
                        {!editStopForm.image && (
                            <ImageActionButton component="label" disabled={!hasWritePerm}>
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) setEditStopForm((p) => ({ ...p, image: URL.createObjectURL(file), image_file: file }));
                                    }}
                                />
                            </ImageActionButton>
                        )}

                        <Divider />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontSize: 18, fontWeight: 500 }}>
                                Nearby Attractions
                            </Typography>
                            {(editStopForm.attractions || []).map((a, idx) => (
                                <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <RemoteAutocomplete
                                            resource="attraction"
                                            urlSuffix={null}
                                            label="Attraction"
                                            variant="filled"
                                            size="small"
                                            value={a.attraction_id != null ? { id: a.attraction_id, name: a.name } : null}
                                            onChange={(ev, value) =>
                                                setEditStopForm((p) => ({
                                                    ...p,
                                                    attractions: p.attractions.map((att, i) =>
                                                        i === idx
                                                            ? { ...att, attraction_id: value?.id ?? null, name: value?.name || '' }
                                                            : att
                                                    ),
                                                }))
                                            }
                                            getOptionLabel={(opt) => opt?.name || ''}
                                            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                            optional
                                        />
                                    </Box>
                                    <TextField
                                        variant="filled"
                                        label="Distance (m)"
                                        type="number"
                                        value={a.distance}
                                        onChange={(e) =>
                                            setEditStopForm((p) => ({
                                                ...p,
                                                attractions: p.attractions.map((att, i) =>
                                                    i === idx ? { ...att, distance: e.target.value } : att
                                                ),
                                            }))
                                        }
                                        size="small"
                                        sx={{ width: 100 }}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <DeleteIconButton
                                        title="Remove attraction"
                                        allowed={hasWritePerm}
                                        onClick={() =>
                                            setEditStopForm((p) => ({
                                                ...p,
                                                attractions: p.attractions.filter((_, i) => i !== idx),
                                            }))
                                        }
                                    />
                                </Box>
                            ))}
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                    setEditStopForm((p) => ({
                                        ...p,
                                        attractions: [...p.attractions, { attraction_id: null, name: '', sequence: p.attractions.length + 1, distance: '' }],
                                    }))
                                }
                                sx={{ alignSelf: 'flex-start', mt: 1 }}
                            >
                                ADD ATTRACTION
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ borderTop: '1px solid #d8d8d8', p: 2, display: 'flex', gap: 1, position: 'sticky', bottom: 0, bgcolor: 'background.paper', zIndex: 2 }}>
                        {/* Not "Save": this only updates the line set form; its page SAVE persists it. */}
                        <FormActionButtons
                            mode="edit"
                            submitLabel="Apply"
                            submitIcon={null}
                            onSubmit={saveStopEdit}
                            submitDisabled={!editStopForm.name}
                            onCancel={requestCloseStopDrawer}
                            size="medium"
                            sx={{ flex: 1 }}
                        />
                    </Box>
                </Box>
            </Drawer>
            <LineSettingsDialog
                open={lineSettingsOpen}
                route={lineSettingsRouteIndex !== null ? lineSet.routes[lineSettingsRouteIndex] : null}
                onSave={onLineSettingsSave}
                onDirtyChange={setLineSettingsDirty}
                onClose={() => {
                    setLineSettingsOpen(false);
                    updateUrlParams({ settings: null });
                }}
            />
            <Popover
                open={Boolean(vehicleListAnchor)}
                anchorEl={vehicleListAnchor}
                onClose={() => setVehicleListAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box sx={{ width: 380, maxHeight: 420, overflow: 'auto' }}>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Vehicles ({visibleVehicles.length})
                        </Typography>
                        <IconButton size="small" onClick={() => setVehicleListAnchor(null)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    {visibleVehicles.length === 0 ? (
                        <Typography sx={{ p: 2, color: 'text.secondary' }}>No vehicles found</Typography>
                    ) : (
                        <List dense disablePadding>
                            {visibleVehicles.map((v, idx) => (
                                <ListItem
                                    key={v.id}
                                    divider={idx < visibleVehicles.length - 1}
                                    sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 2, py: 1 }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {v.name || `Vehicle #${v.id}`}
                                        </Typography>
                                        <Chip
                                            label={VEHICLE_STATUS_LABELS[v.status] || v.status}
                                            color={VEHICLE_STATUS_CHIP_COLORS[v.status] || 'default'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.5, width: '100%' }}>
                                        {v.license_plate && (
                                            <Typography variant="caption" color="text.secondary">
                                                Plate: <strong>{v.license_plate}</strong>
                                            </Typography>
                                        )}
                                        {v.route_name && (
                                            <Typography variant="caption" color="text.secondary">
                                                Line: {v.route_name}
                                            </Typography>
                                        )}
                                        {v.speed != null && v.status === 'moving' && (
                                            <Typography variant="caption" color="text.secondary">
                                                Speed: {Math.round(v.speed)} km/h
                                            </Typography>
                                        )}
                                        {Array.isArray(v.route_hints) && v.route_hints.length > 0 && (
                                            <Typography variant="caption" color="text.secondary">
                                                Hints: {v.route_hints.join(', ')}
                                            </Typography>
                                        )}
                                        {v.route_assignment_ts && (
                                            <Typography variant="caption" color="text.secondary" title={v.route_assignment_ts}>
                                                Assigned: {formatAge(v.route_assignment_ts)}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary" title={v.location_ts || ''}>
                                            Updated: {formatAge(v.location_ts)}
                                        </Typography>
                                    </Box>
                                    <Link
                                        href={`/bus-tours/vehicles/${v.id}`}
                                        variant="caption"
                                        sx={{ mt: 0.5 }}
                                        underline="hover"
                                    >
                                        View vehicle details
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </Popover>

            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 1,
                        p: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <IconButton onClick={() => history.push('/bus-tours/line-sets')}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>{isNew ? 'Create New Line Set' : lineSet.name}</Typography>
                    </Box>
                </Box>

                {/* Line Set Details */}
                <Box sx={{ mx: 2, mb: 2, p: 3, border: '1px solid #d8d8d8', borderRadius: 1.5, bgcolor: 'white' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Line Set Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <LicenseeFormField value={lineSet.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="filled"
                                label="Line Set Name"
                                value={lineSet.name}
                                onChange={(e) => { const { value } = e.target; setLineSet((prev) => ({ ...prev, name: value })); }}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <RemoteAutocomplete
                                resource="access_tag"
                                urlSuffix={null}
                                label="Access Groups"
                                variant="filled"
                                size="small"
                                multiple
                                value={lineSet.atag || []}
                                onChange={(ev, value) => setLineSet((prev) => ({ ...prev, atag: value }))}
                                getOptionLabel={(opt) => opt?.name || ''}
                                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                optional
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SystemTagsDropdown
                                label="Tags"
                                value={lineSet.tags || []}
                                onChange={(ev, value) => setLineSet((prev) => ({ ...prev, tags: value }))}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Lines Section */}
                <Box sx={{ mx: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">Lines</Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={addNewLine}>
                        ADD LINE
                    </Button>
                </Box>

                {lineSet.routes.length > 0 && (
                    <Box sx={{ mx: 2, flex: 1, border: '1px solid #d8d8d8', borderRadius: 1.5, bgcolor: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {/* Tabs */}
                        <Box sx={{ borderBottom: '1px solid #d8d8d8' }}>
                            <Tabs
                                value={activeTab}
                                onChange={(e, v) => {
                                    setActiveTab(v);
                                    // Reset map viewport to the new route's bounding box.
                                    setResetViewportKey((k) => k + 1);
                                    // Reflect the new tab in the URL; clear any open
                                    // drawers since they're scoped to the previous tab.
                                    updateUrlParams({
                                        route: lineSet.routes[v]?.id ?? null,
                                        settings: null,
                                        stop: null,
                                    });
                                }}
                                variant="scrollable"
                                scrollButtons="auto"
                                TabIndicatorProps={{ sx: { height: 3, bgcolor: activeTabColor } }}
                                sx={{
                                    // Keep the label neutral so the indicator is the
                                    // only highlight color (MUI would tint it blue).
                                    '& .MuiTab-root.Mui-selected': { color: 'text.primary', fontWeight: 600 },
                                }}
                            >
                                {lineSet.routes.map((route, idx) => (
                                    <Tab
                                        key={idx}
                                        // A div, not a <button>: the label holds the move-line IconButtons.
                                        component="div"
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                <IconButton
                                                    size="small"
                                                    disabled={idx === 0}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        moveLine(idx, 'left');
                                                    }}
                                                    aria-label="move line left"
                                                    sx={{ p: 0.25 }}
                                                >
                                                    <ChevronLeftIcon fontSize="small" />
                                                </IconButton>
                                                <span>{route.name || `Line ${idx + 1}`}</span>
                                                <IconButton
                                                    size="small"
                                                    disabled={idx === lineSet.routes.length - 1}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        moveLine(idx, 'right');
                                                    }}
                                                    aria-label="move line right"
                                                    sx={{ p: 0.25 }}
                                                >
                                                    <ChevronRightIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        }
                                    />
                                ))}
                                {!isNew && <Tab key="used-by" label="Used by" />}
                            </Tabs>
                        </Box>

                        {/* Tab Content */}
                        {!isNew && activeTab === lineSet.routes.length && (
                            <Box sx={{ p: 3 }}>
                                <UsedByPanel resource="route_set" id={id} />
                            </Box>
                        )}
                        {activeRoute && activeTab < lineSet.routes.length && (
                            <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                                {/* Controls */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        justifyContent: 'space-between',
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        gap: 1,
                                        mb: 2,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, rowGap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="h6">Bus Stops</Typography>
                                        {!isNew && (<>
                                            <ToggleButtonGroup
                                                value={mapLayer}
                                                exclusive
                                                onChange={(e, val) => { if (val) { setMapLayer(val); setResetViewportKey((k) => k + 1); } }}
                                                size="small"
                                            >
                                                <ToggleButton value="stops" aria-label="show stops">
                                                    <PlaceIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                    Stops
                                                </ToggleButton>
                                                <ToggleButton value="vehicles" aria-label="show vehicles">
                                                    <DirectionsBusIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                    Vehicles
                                                </ToggleButton>
                                                <ToggleButton value="both" aria-label="show both">
                                                    <PlaceIcon fontSize="small" sx={{ mr: 0.25 }} />
                                                    <DirectionsBusIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                    Both
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                            {refreshCountdown != null && (
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    onClick={refreshVehiclesNow}
                                                    sx={{ color: 'text.secondary', minWidth: 28, textTransform: 'none', px: 0.5 }}
                                                    title="Click to refresh now"
                                                >
                                                    {refreshCountdown}s
                                                </Button>
                                            )}
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={showKml}
                                                        onChange={(e) => setShowKml(e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label="KML"
                                                sx={{ ml: 0 }}
                                            />
                                            {mapLayer !== 'stops' && (<>
                                                <Checkbox
                                                    checked={showOutOfBounds}
                                                    onChange={(e) => setShowOutOfBounds(e.target.checked)}
                                                    size="small"
                                                    title="Show vehicles outside bounding box"
                                                    sx={{ p: 0.5 }}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={showAllRouteVehicles}
                                                            onChange={(e) => setShowAllRouteVehicles(e.target.checked)}
                                                            size="small"
                                                        />
                                                    }
                                                    label="All lines"
                                                    sx={{ ml: 0 }}
                                                />
                                            </>)}
                                            {mapViewportDirty && (
                                                <IconButton
                                                    size="small"
                                                    title="Reset map viewport"
                                                    onClick={() => setResetViewportKey((k) => k + 1)}
                                                >
                                                    <CropFreeIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {visibleVehicles.length > 0 && (
                                                <IconButton
                                                    size="small"
                                                    title="Vehicle list"
                                                    onClick={(e) => setVehicleListAnchor(e.currentTarget)}
                                                >
                                                    <ListAltIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </>)}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <Box sx={{ border: '1px solid #d0d5dd', borderRadius: 1, px: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="body2">Route status:</Typography>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={activeRoute.enabled}
                                                        onChange={(e) => updateRouteEnabled(activeTab, e.target.checked)}
                                                        size="small"
                                                    />
                                                }
                                                label={activeRoute.enabled ? 'Active' : 'Inactive'}
                                            />
                                        </Box>
                                        <DeleteButton allowed={hasWritePerm} onClick={() => removeLine(activeTab)}>
                                            REMOVE LINE
                                        </DeleteButton>
                                        <Button
                                            variant="outlined"
                                            onClick={() => openLineSettings(activeTab)}
                                        >
                                            LINE SETTINGS
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Split: Map + Stops */}
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={7}>
                                        <BusStopsMap
                                            googleApiKey={settings?.google_maps_key}
                                            stops={activeRoute.stops || []}
                                            vehicles={mapLayer !== 'stops' ? visibleVehicles : undefined}
                                            routeColors={routeColors}
                                            showStops={mapLayer !== 'vehicles'}
                                            color={activeRoute.color || '#007AFF'}
                                            kml={showKml ? activeRoute.kml : null}
                                            kmlUrl={showKml && typeof activeRoute.kml_file === 'string' ? activeRoute.kml_file : null}
                                            onStopClick={openStopEdit}
                                            onViewportDirty={setMapViewportDirty}
                                            resetViewportKey={resetViewportKey}
                                            routeBoundingBox={activeRoute.bounding_box}
                                            routeBoundingBoxLabel={activeRoute.name}
                                            routeSetBoundingBox={routeSetBoundingBox}
                                            routeSetBoundingBoxLabel={lineSet.name}
                                            height={500}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Box sx={{ border: '1px solid #d8d8d8', borderRadius: 1, height: 500, display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ flex: 1, overflow: 'auto' }}>
                                                <List dense>
                                                    {activeRoute.stops?.map((link, idx) => (
                                                        <ListItem
                                                            key={idx}
                                                            divider
                                                            disablePadding
                                                            // Reserve room for the action icons — the default
                                                            // secondaryAction padding (48px) only fits one button,
                                                            // so the icons overlap the text on narrow screens.
                                                            sx={{ pl: 2, pr: { xs: 10, sm: 17 } }}
                                                            secondaryAction={
                                                                <Box
                                                                    sx={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns: { xs: 'repeat(2, auto)', sm: 'repeat(4, auto)' },
                                                                        alignItems: 'center',
                                                                        justifyItems: 'center',
                                                                    }}
                                                                >
                                                                    <IconButton size="small" disabled={idx === 0} onClick={() => moveStop(idx, 'up')}>
                                                                        <ArrowUpwardIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" disabled={idx === activeRoute.stops.length - 1} onClick={() => moveStop(idx, 'down')}>
                                                                        <ArrowDownwardIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" onClick={() => openStopEdit(idx)}>
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <DeleteIconButton title="Remove stop" allowed={hasWritePerm} onClick={() => removeStop(idx)} />
                                                                </Box>
                                                            }
                                                        >
                                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                                <Box
                                                                    sx={{
                                                                        width: 28,
                                                                        height: 32,
                                                                        bgcolor: activeRoute.color || '#007AFF',
                                                                        borderRadius: '50% 50% 50% 0',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        color: 'white',
                                                                        fontWeight: 'bold',
                                                                        fontSize: 14,
                                                                    }}
                                                                >
                                                                    {idx + 1}
                                                                </Box>
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={<>{link.stop?.name || `Stop ${idx + 1}`}<StopWarningIcon stop={link.stop} /></>}
                                                                secondary={[
                                                                    link.stop?.latitude && link.stop?.longitude
                                                                        ? `${Number(link.stop.latitude).toFixed(6)}, ${Number(link.stop.longitude).toFixed(6)}`
                                                                        : '',
                                                                    link.stop?.label ? `Pin: ${link.stop.label}` : '',
                                                                ].filter(Boolean).join(' | ') || undefined}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                            <Box sx={{ p: 1.5 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddIcon />}
                                                    fullWidth
                                                    onClick={openStopAdd}
                                                >
                                                    ADD STOP
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Bottom Bar */}
                <Box sx={{ borderTop: '1px solid #d8d8d8', bgcolor: '#f5f5f5', p: 1.5, px: 3, mt: 'auto' }}>
                    <FormActionButtons
                        mode={isNew ? 'create' : 'edit'}
                        // CP-8423: the standard footer DELETE (was REMOVE LINE SET in the header).
                        onDelete={!isNew ? () => setDeleteConfirmOpen(true) : undefined}
                        deletePerm="vehicle_fleet.delete"
                        onSubmit={onSave}
                        // CP-8441: an existing line set's SAVE waits for a change to the page itself.
                        submitDisabled={saving || !lineSet.name || (!isNew && isEqual(lineSet, savedLineSet))}
                        onCancel={() => history.push('/bus-tours/line-sets')}
                        size="medium"
                    />
                </Box>
            </Box>
        </FormLicenseeContext.Provider>
    );
};

export default LineSetEdit;
